import React, { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { TestimonialForm } from "./TestimonialForm";

const STATUS = {
  pending: ["Waiting for approval", "bg-amber-100 border-amber-700 text-amber-900"],
  approved: ["Live", "bg-emerald-100 border-emerald-700 text-emerald-800"],
  rejected: ["Not approved", "bg-red-100 border-red-700 text-red-800"],
};
const BTN = "border-2 border-black bg-white px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white disabled:opacity-40";

function RejectBox({ t, busy, onReject, onCancel }) {
  const [reason, setReason] = useState("");
  return (
    <div className="mt-2 flex flex-wrap gap-1.5 items-center" data-testid={`testimonial-reject-box-${t.id}`}>
      <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} placeholder="Reason (shown to the buyer)"
             className="border-2 border-black px-2 py-1 text-xs flex-1 min-w-[180px]" data-testid={`testimonial-reject-reason-${t.id}`} />
      <button type="button" disabled={busy} onClick={() => onReject(reason)} className={BTN} data-testid={`testimonial-reject-confirm-${t.id}`}>Reject</button>
      <button type="button" onClick={onCancel} className={BTN} data-testid={`testimonial-reject-cancel-${t.id}`}>Cancel</button>
    </div>
  );
}

export function TestimonialItem({ t, admin, showFirm, onChanged }) {
  const [mode, setMode] = useState(null);
  const [busy, setBusy] = useState(false);
  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); setMode(null); onChanged(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const review = (action, reason) => act(() => api.post(`/testimonials/${t.id}/review`, { action, reason }), action === "approve" ? "Approved — it's live now" : "Rejected");

  if (mode === "edit") {
    return <TestimonialForm initial={t} admin={admin} testId={`testimonial-edit-${t.id}`}
                            onSaved={() => { setMode(null); onChanged(); }} onCancel={() => setMode(null)} />;
  }
  const [label, cls] = STATUS[t.status] || STATUS.pending;
  return (
    <div className="border-2 border-black bg-white p-3" data-testid={`testimonial-item-${t.id}`}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
        {t.photo_url && <img src={`${process.env.REACT_APP_BACKEND_URL || ""}${t.photo_url}`} alt="" className="h-8 w-8 rounded-full border border-black object-cover" data-testid={`testimonial-photo-${t.id}`} />}
        <span className="font-bold">{t.name}</span>
        {t.case_tag && <span className="text-zinc-500">· {t.case_tag}</span>}
        <span className="text-amber-500">{"★".repeat(t.stars)}</span>
        {showFirm && t.firm_name && <span className="label-mono">{t.firm_name}</span>}
        <span className={`ml-auto border px-1.5 py-0.5 text-[9px] uppercase tracking-widest ${cls}`} data-testid={`testimonial-status-${t.id}`}>{label}</span>
      </div>
      <p className="mt-2 text-sm leading-relaxed">&ldquo;{t.quote}&rdquo;</p>
      {t.status === "rejected" && t.reject_reason && <p className="mt-1 text-[11px] text-red-700" data-testid={`testimonial-reject-note-${t.id}`}>Reason: {t.reject_reason}</p>}
      {mode === "reject" ? (
        <RejectBox t={t} busy={busy} onReject={(r) => review("reject", r)} onCancel={() => setMode(null)} />
      ) : (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {admin && t.status !== "approved" && <button type="button" disabled={busy} onClick={() => review("approve")} className={BTN} data-testid={`testimonial-approve-${t.id}`}>Approve</button>}
          {admin && t.status !== "rejected" && <button type="button" onClick={() => setMode("reject")} className={BTN} data-testid={`testimonial-reject-${t.id}`}>Reject</button>}
          <button type="button" onClick={() => setMode("edit")} className={BTN} data-testid={`testimonial-edit-btn-${t.id}`}>Edit</button>
          {mode === "delete"
            ? <button type="button" disabled={busy} onClick={() => act(() => api.delete(`/testimonials/${t.id}`), "Testimonial removed")} className={`${BTN} !bg-red-600 !text-white`} data-testid={`testimonial-delete-confirm-${t.id}`}>Confirm remove</button>
            : <button type="button" onClick={() => setMode("delete")} className={BTN} data-testid={`testimonial-delete-${t.id}`}>Remove</button>}
        </div>
      )}
    </div>
  );
}
